'use client';

/**
 * `useRhythmRun`, drives one pass in time — `S4` and `S5`.
 *
 * ## What it no longer does
 *
 * It used to build an expected sequence, subscribe to the piano to run a
 * partial validation on **every press**, detect bursts of errors, cancel the
 * pass mid-flight, and finally grade the run against an absolute clock. All
 * of that served a gate that, measured over 10 528 real work units, failed a
 * clean player 15.0% of the time and passed a player with no rhythm at all
 * 62.2% of the time. The gate is gone and so is its machinery — see
 * `components/learn-piece/rhythm.ts`.
 *
 * What is left is a lifecycle and a reading. The hook starts the range,
 * follows the playhead, and when the pass ends hands back a `RunReading`:
 * which onsets were answered, whether the player stopped, and how steady
 * their own pulse was. **Nothing here decides anything** — the view says what
 * happened, and advancing the journey is the player's act.
 *
 * ## Why the metronome is synced, and what it clicks
 *
 * The song lanes are muted (unless the player asked to hear them), so the
 * click is the only beat reference. It runs in *synced* mode: the free
 * scheduler anchors its grid on `clock.currentTime + 100 ms`, an arbitrary
 * phase off the downbeat that varies run to run, which made in-time playing
 * read as late. Synced mode schedules every click on the transport's own beat
 * ticks, so the audible grid and the score's grid are the same by
 * construction.
 *
 * Its signature is read from the **transport timeline**, not derived from
 * `ticksPerBar / ppq`. That derivation returned 3 in 6/8, and the scheduler
 * then computed a bar of half the real length: the accent landed on the wrong
 * beat every other bar, on the 9.4% of units (987 / 10 530) in a compound
 * metre.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStudio } from '@/contexts/studio-context';
import { track } from '@/lib/analytics';
import { renderTrail } from '@/lib/analytics/trail';
import { logRunReport } from '@/lib/learning-journey/debug/run-report-logger';
import { msPerBeat } from '@/lib/learning-journey/runtime/timing';
import {
  handsAsked,
  onsetTimes,
  passOutcome,
  pulseDivision,
  runReading,
  runSubject,
  StrayWatch,
  type RunClock,
  type RunReading,
} from '@/components/learn-piece/rhythm';
import type { RecordedPress } from '@/lib/learning-journey/types';
import type { PracticeGroup } from '@/components/learn-piece/practice';
import type { LearningJourney } from '@/lib/learning-journey/journey';
import {
  RunStalledError,
  type RangeRunner,
  type RunOutcome,
  type RunPhase,
} from '@/lib/learning-journey/runtime/range-runner';
import type { Studio } from '@/lib/studio/studio';
import type { RunRange } from '@/lib/learning-journey/types';
import type { SongControler } from '@/lib/track/controler/song-controler';

// ── Public types ─────────────────────────────────────────────────

export type RhythmRunStatus = 'idle' | 'running';

/** What a finished pass left behind. Never produced by a cancel. */
export type FinishedRun = {
  readonly reading: RunReading;
  /** The rate the pass was played at, so the view can report it. */
  readonly rate: number;
};

export type UseRhythmRunOptions = {
  readonly journey: LearningJourney | null;
  readonly rangeRunner: RangeRunner | null;
  /** The span played. A unit in S4, a junction window in S5. */
  readonly range: RunRange;
  /** The onsets of that span, for the reading and the run map. */
  readonly groups: readonly PracticeGroup[];
  /** Tempo written in the song, in BPM. */
  readonly bpm: number;
  readonly ppq: number;
  /** Ticks per bar at the range's metre, for the bar the panel names. */
  readonly ticksPerBar: number;
  /** Fraction of the written tempo the player chose. */
  readonly rate: number;
  /** Which step this pass belongs to. Calibration log only. */
  readonly state: 'S4' | 'S5' | 'phase2';
  /**
   * Read the pass on time only, whatever keys were struck.
   *
   * The "beat it out" act: the roll is already a rhythm notation — time
   * against pitch — so striking any key as each note crosses the playhead is
   * the exercise, and the reading has to stop asking about pitch to match it.
   */
  readonly ignorePitch?: boolean;
  /**
   * `false` runs the pass without the continuous click.
   *
   * It costs the reading nothing, and that is worth stating: `RunClock` is
   * anchored on the transport's first tick, `onsetTimes` derives the attack
   * grid from it, and `findInterruptions` reads only that grid and the
   * presses. The metronome never enters any of it. The count-in survives too
   * — it is `CountInComponent`, scheduled sample-accurately on the audio
   * clock, a different object entirely. The click gives the departure, not
   * the journey.
   */
  readonly metronome?: boolean;
  /** When `true`, the song plays under the pass instead of being muted. */
  readonly auditionSong: boolean;
  /**
   * Hand the application takes during the pass, sounded from the song's own
   * lanes. `null` mutes the song whole, which is the ordinary case.
   */
  readonly accompanyHand: 'left' | 'right' | null;
  readonly songControler: SongControler | null;
  /**
   * Called once, when a pass settles. The end of a run is an event, not a
   * value to derive: a view that watched `lastRun` in an effect to count the
   * pass would be reacting to its own render.
   */
  readonly onFinished?: (run: FinishedRun) => void;
};

export type UseRhythmRunResult = {
  readonly status: RhythmRunStatus;
  readonly start: () => void;
  readonly cancel: () => void;
  /** Drops the last reading so the view can offer to play again. */
  readonly clear: () => void;
  readonly lastRun: FinishedRun | null;
  /**
   * How many onsets the **playhead** has gone past.
   *
   * Not how many the player answered: the map has to say *where the passage
   * is*, which is the one thing a beginner loses when they fumble. Advancing
   * it on presses would freeze the map exactly when it is needed.
   */
  readonly playedIndex: number;
  /**
   * Where the playhead is, in ticks. `null` before the first tick of a pass.
   *
   * The bar the panel names comes from here and not from the last onset gone
   * by: on a unit with four attacks over four bars, an onset-derived bar
   * number would sit still for a whole bar and then jump two.
   */
  readonly playedTick: number | null;
  /**
   * Keys struck during the pass that the passage asks for nowhere near their
   * instant, as they happen.
   *
   * Live and not derived from the reading: six bars later the player no
   * longer knows which key was the wrong one, and a count that only appears
   * at the end teaches nothing about where it came from. Read with the very
   * rule the reading uses when the pass settles (`StrayWatch`), so the number
   * on screen during the pass and the number under the verdict cannot
   * disagree.
   */
  readonly strays: LiveStrays;
  /** See {@link RunDebug}. `null` in production and before the first pass. */
  readonly debug: RunDebug | null;
  /**
   * The last pass the runner's watchdog had to stop, until the next one is
   * armed. See {@link RunStall}.
   */
  readonly stalled: RunStall | null;
};

/**
 * A pass that stopped making progress and was settled by the watchdog.
 *
 * Not a `FinishedRun`: no reading can be made of a pass the playhead never
 * crossed, and storing it as one would put the view in the state that
 * follows a pass. But not nothing either, which is what a cancelled pass
 * is: the player pressed and the app went quiet, and the view owes them a
 * sentence about it. The founding case is an iPad back from a long sleep
 * with its audio context left `interrupted` — see `range-runner.ts`.
 */
export type RunStall = {
  readonly phase: RunPhase;
  readonly audioState: string;
};

export type LiveStrays = {
  readonly count: number;
  /** Struck within the last instants, for the key to light. */
  readonly flashing: readonly number[];
};

/**
 * What a pass looked like from underneath — dev only, and shown on the
 * panel rather than logged.
 *
 * The reading says *what* it concluded ("tu t'es arrêté à la mesure 3"); it
 * cannot say *why*, and the two candidate causes of a wrong conclusion are
 * invisible from the outside: keys that never reached the capture, and a
 * playhead that stopped or jumped mid-pass while the grid the reading uses
 * kept running. Both are one number here. On a tablet there is no console to
 * read them from, which is the whole reason this is a value and not a log.
 */
export type RunDebug = {
  /** Presses the capture held, at or after the run anchor. */
  readonly presses: number;
  /** Presses it held from *before* the anchor — count-in, or a late anchor. */
  readonly pressesBeforeAnchor: number;
  readonly onsets: number;
  readonly honoured: number;
  readonly strays: number;
  /** Beat of the first stop the reading named, or `null`. */
  readonly stopAtBeat: number | null;
  /**
   * The silence the stop was read in: the last press before it and the
   * first one after, in beats. This is the pair `findInterruptions`
   * actually measures, and the only way to tell a hole the player left
   * from a hole the reading framed.
   */
  readonly stopFromBeat: number | null;
  readonly stopToBeat: number | null;

  /** Wall time the pass took, in seconds. */
  readonly seconds: number;
  /** Longest stretch the playhead did not move, in ms. */
  readonly playheadFrozenMs: number;
  /** Largest backward jump of the playhead, in ticks. `0` when monotonic. */
  readonly playheadJumpedBack: number;
};

/** How long a wrong key stays lit. */
const STRAY_FLASH_MS = 220;

const NO_STRAYS: readonly number[] = [];

/**
 * Total audible output latency of the `AudioContext`, in milliseconds.
 *
 * Players press in time with what they *hear*, and what they hear reaches
 * them one output buffer after it is scheduled. Without this the reading
 * would call every in-time press systematically late. `outputLatency` covers
 * the whole path and is unsupported on Safari, where `baseLatency` (the
 * context's own processing) is the honest fallback.
 */
function readOutputLatencyMs(studio: Studio | null): number {
  if (studio === null) {
    return 0;
  }

  const ctx = studio.getAudioContext();
  const seconds = ctx.outputLatency || ctx.baseLatency || 0;

  return seconds * 1000;
}

// ── Implementation ───────────────────────────────────────────────

export function useRhythmRun(opts: UseRhythmRunOptions): UseRhythmRunResult {
  const {
    journey,
    rangeRunner,
    range,
    groups,
    bpm,
    ppq,
    ticksPerBar,
    rate,
    state,
    ignorePitch,
    metronome = true,
    auditionSong,
    accompanyHand,
    songControler,
    onFinished,
  } = opts;

  const { studio } = useStudio();

  const [status, setStatus] = useState<RhythmRunStatus>('idle');
  const [lastRun, setLastRun] = useState<FinishedRun | null>(null);
  const [playedIndex, setPlayedIndex] = useState(0);
  const [playedTick, setPlayedTick] = useState<number | null>(null);

  const [strayCount, setStrayCount] = useState(0);
  const [stalled, setStalled] = useState<RunStall | null>(null);
  const [strayFlash, setStrayFlash] = useState<readonly number[]>(NO_STRAYS);
  const [debug, setDebug] = useState<RunDebug | null>(null);

  /**
   * A reading survives its own pass, and must not survive its subject.
   *
   * Adjusted during render rather than in an effect, the pattern the views
   * already use for the same kind of state (`countedRung` in `rhythm-state`):
   * an effect would paint one frame of the previous unit's verdict before
   * correcting itself, and that frame is the whole bug. See `runSubject`.
   */
  const subject = runSubject(range, state);
  const [readingOf, setReadingOf] = useState(subject);

  if (readingOf !== subject) {
    setReadingOf(subject);
    setLastRun(null);
    setPlayedIndex(0);
    setPlayedTick(null);
    setStrayCount(0);
    setStalled(null);
    setStrayFlash(NO_STRAYS);
    setDebug(null);
  }

  const clockRef = useRef<RunClock | null>(null);
  const runIdRef = useRef(0);
  const gridBeforeRef = useRef(false);
  // Un `Set` et non un tableau : chaque minuterie s'y retire elle-même en
  // expirant, sinon la liste grossit d'une entrée par fausse note.
  const strayTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
  /**
   * Held in a ref rather than closed over by the pass alone: the run promise
   * settles the subscription on every ordinary path, but a rejection has no
   * handler here (and never had), and a listener left on the piano bus would
   * go on counting keys for a pass that is over.
   */
  const strayUnsubRef = useRef<(() => void) | null>(null);

  const stopStrayWatch = useCallback(() => {
    strayUnsubRef.current?.();
    strayUnsubRef.current = null;
  }, []);

  const clearStrayFlashes = useCallback(() => {
    for (const timer of strayTimersRef.current) {
      clearTimeout(timer);
    }

    strayTimersRef.current.clear();
    setStrayFlash(NO_STRAYS);
  }, []);

  useEffect(() => {
    return () => {
      clearStrayFlashes();
      stopStrayWatch();
    };
  }, [clearStrayFlashes, stopStrayWatch]);

  // Une passe ne survit pas à la vue qui l'a lancée : sans cela le transport,
  // la boucle du bridge et le chien de garde continuaient après le démontage,
  // et le `start()` suivant tombait sur un `RangeRunnerBusyError`. Annuler
  // règle la promesse, dont le `.then` remet le reste à zéro via `settle`.
  useEffect(() => {
    if (rangeRunner === null) {
      return;
    }

    return () => {
      rangeRunner.cancel();
    };
  }, [rangeRunner]);

  /**
   * What the song sounds during the pass.
   *
   * Three states and not two: everything (the player asked to hear the
   * recording), one hand (the application accompanies), or nothing (the
   * ordinary case — the click is the only reference and the player drives
   * the sound).
   */
  const setSongVoice = useCallback(
    (voice: 'all' | 'accompaniment' | 'none') => {
      if (songControler === null) {
        return;
      }

      if (voice === 'accompaniment' && accompanyHand !== null) {
        songControler.soloHand(accompanyHand);

        return;
      }

      songControler.setMuted(voice === 'none');
    },
    [songControler, accompanyHand],
  );

  const setMetronomeActive = useCallback(
    (active: boolean) => {
      if (studio === null) {
        return;
      }

      // Named `click`, not `metronome`: the hook's own `metronome` option —
      // whether this pass wants one at all — is in scope three lines up, and
      // two identifiers of opposite meaning under the same name is a trap.
      const click = studio.continuousMetronome;

      if (!active) {
        click.stop();

        return;
      }

      const timeline = studio.getLocalTransport().transport.timeline;
      const signature = timeline.getTimeSignatureAtTick(range.startTick);

      click.setSignature({
        num: Math.max(1, signature.numerator),
        den: signature.denominator,
      });
      click.setDivision(pulseDivision(bpm * rate));
      // Scheduling follows the transport timeline, but the journey metronome
      // bridge reads `click.bpm` to stamp `beatDurationMs`: keep it true.
      click.setBpm(bpm * rate);
      click.startSynced(studio.getLocalTransport());
    },
    [studio, bpm, rate, range.startTick],
  );

  /**
   * The pulse, seen.
   *
   * The roll already draws a full rhythmic grid — bar lines, beat lines,
   * dashed subdivisions, and the **beat number** in the left gutter
   * (`piano-roll/measures.ts`) — and it is switched off. `setMetronomeEnabled`
   * turns it on together with the click, but this hook drives
   * `continuousMetronome` directly and never touched the flag, so during a
   * pass the click was audible and the grid was dark.
   *
   * So the visible pulse this step needs is not a widget to invent in a
   * panel: it is a line that crosses 1348 px of roll, next to the notes, and
   * it exists. A 3 px ribbon 700 px from where the eyes are is not perceived
   * in peripheral vision; this is.
   *
   * The player's own setting is restored afterwards — a pass borrows the
   * grid, it does not decide for them.
   */
  const setBeatGridVisible = useCallback(
    (visible: boolean) => {
      if (studio === null) {
        return;
      }

      if (visible) {
        gridBeforeRef.current = studio.highlightActiveBeat.value;
        studio.setHighlightActiveBeat(true);

        return;
      }

      studio.setHighlightActiveBeat(gridBeforeRef.current);
    },
    [studio],
  );

  // ── Run lifecycle ──────────────────────────────────────────────

  /**
   * The watchdog stopped the pass: keep what it saw for the view, and send
   * it, because this is the one failure no error tracker will ever see —
   * nothing threw. `pageVisible` lets the dashboard set aside the passes
   * that stalled because the page went to the background, which is not a
   * fault; the trail says what the page and the audio did just before.
   */
  const reportStall = useCallback((error: RunStalledError) => {
    setStalled({ phase: error.phase, audioState: error.audioState });

    track({
      name: 'run_stalled',
      properties: {
        phase: error.phase,
        stalledForMs: Math.round(error.stalledForMs),
        audioState: error.audioState,
        audioMoving: error.audioMoving,
        pageVisible:
          typeof document === 'undefined' || document.visibilityState === 'visible',
        trail: renderTrail() ?? undefined,
      },
    });
  }, []);

  const start = useCallback(() => {
    if (journey === null || rangeRunner === null || status !== 'idle') {
      return;
    }

    // Un autre passage tient déjà le transport (audition de S0, vue
    // précédente pas encore démontée) : `run()` jetterait en synchrone,
    // après que l'on a armé la capture et lancé le métronome.
    if (rangeRunner.isRunning()) {
      return;
    }

    const myRunId = runIdRef.current + 1;
    runIdRef.current = myRunId;

    clockRef.current = null;
    setLastRun(null);
    setPlayedIndex(0);
    setPlayedTick(null);
    setStrayCount(0);
    setStalled(null);
    setDebug(null);
    clearStrayFlashes();
    stopStrayWatch();
    setStatus('running');

    // Empty the capture here, while the pass is only armed. Doing it on the
    // first tick — where the anchor is set — erased the downbeat the player
    // had just played on time.
    journey.armRunCapture();

    // Wipe what the previous pass drew. The live piano lane is cumulative —
    // in free play that is the whole point — but here every attempt drew on
    // top of the last, and the grey it draws in sits at 1.03:1 against the
    // left hand, i.e. the same object in luminance. Three attempts in, the
    // player is reading their own history instead of the notation.
    studio?.clearLivePiano();

    journey.startRhythmRun();
    setSongVoice(
      auditionSong
        ? 'all'
        : accompanyHand === null
          ? 'none'
          : 'accompaniment',
    );
    setMetronomeActive(metronome);
    setBeatGridVisible(true);

    const beatMs = msPerBeat(bpm, rate);
    const audioLatencyMs = readOutputLatencyMs(studio);

    /**
     * Armed on the first tick, because until the playhead has moved there is
     * no reference frame to call a key early, late, or wrong. Presses made
     * during the count-in therefore reach nothing — which is right: the
     * count-in is where a player settles their hands on the keys.
     */
    let watch: StrayWatch | null = null;

    strayUnsubRef.current = ignorePitch
      ? null
      : (studio?.getPiano().on((event) => {
          if (event.type !== 'press' || event.origin !== 'player') {
            return;
          }

          if (watch === null || runIdRef.current !== myRunId) {
            return;
          }

          const midi = event.note.toMidi();

          // Same stamp the capture takes (see `use-journey-piano-bridge`), so
          // the live reading and the settled one measure the same instant.
          if (!watch.isStray(midi, performance.now())) {
            return;
          }

          setStrayCount((n) => n + 1);
          setStrayFlash((lit) => (lit.includes(midi) ? lit : [...lit, midi]));

          const timer = setTimeout(() => {
            strayTimersRef.current.delete(timer);
            setStrayFlash((lit) => lit.filter((m) => m !== midi));
          }, STRAY_FLASH_MS);

          strayTimersRef.current.add(timer);
        }) ?? null);

    let anchored = false;
    let lastIndex = 0;
    let lastBarTick = -1;
    // Playhead watch, for `RunDebug`. A pass reads against a grid that
    // advances on its own; if the transport stalls or is seeked under it,
    // everything after that instant is late by the same amount and the
    // reading calls it a stop. Nothing else in the run would notice.
    const watchPlayhead = WATCH_PLAYHEAD;
    let previousTickMs = 0;
    let previousTick = -1;
    let frozenMs = 0;
    let jumpedBack = 0;

    const handleTick = (tick: number) => {
      if (watchPlayhead && anchored) {
        const nowMs = performance.now();

        if (previousTick >= 0) {
          if (tick === previousTick) {
            frozenMs = Math.max(frozenMs, nowMs - previousTickMs);
          } else {
            if (tick < previousTick) {
              jumpedBack = Math.max(jumpedBack, previousTick - tick);
            }

            previousTickMs = nowMs;
          }
        } else {
          previousTickMs = nowMs;
        }

        previousTick = tick;
      }

      if (!anchored) {
        anchored = true;

        // Anchor on the instant the player *hears* the passage start, not on
        // this callback's raw wall clock. Two systematic offsets would bias
        // every press otherwise: the first tick lands a few ticks past
        // `startTick` (the gap varies run to run), and what is scheduled is
        // heard one output buffer later. Back-project the first, push forward
        // by the second, and only genuine human deviation is left to read.
        const elapsedMs = ppq > 0 ? ((tick - range.startTick) / ppq) * beatMs : 0;
        const anchorMs = performance.now() - elapsedMs + audioLatencyMs;

        journey.beginRunCapture(anchorMs);

        const clock = { anchorMs, beatMs, ppq };
        clockRef.current = clock;
        watch = new StrayWatch(
          groups,
          onsetTimes(groups, clock, range.startTick),
          clock,
        );
      }

      // La tête de lecture ne recule pas pendant une passe : on repart du
      // dernier onset franchi plutôt que de rebalayer depuis zéro à chaque
      // image.
      let index = lastIndex;

      while (index < groups.length && groups[index].startTick <= tick) {
        index += 1;
      }

      if (index !== lastIndex) {
        lastIndex = index;
        setPlayedIndex(index);
      }

      // Quantised to the bar so the panel re-renders a handful of times per
      // pass instead of on every animation frame.
      const barTick =
        ticksPerBar > 0
          ? range.startTick +
            Math.floor((tick - range.startTick) / ticksPerBar) * ticksPerBar
          : tick;

      if (barTick !== lastBarTick) {
        lastBarTick = barTick;
        setPlayedTick(barTick);
      }
    };

    /**
     * A cancelled pass leaves nothing behind.
     *
     * `reading === null` used to be stored as a finished run, and the view
     * read "a pass happened": it took away the keyboard map, dropped the
     * offers that only make sense before playing, and printed no verdict —
     * a screen in the state that follows a pass, for a pass the player
     * stopped before it began. The hook's own contract is the right one:
     * the run simply did not happen.
     */
    const settle = (reading: RunReading | null) => {
      stopStrayWatch();
      clearStrayFlashes();
      setSongVoice('all');
      setMetronomeActive(false);
      setBeatGridVisible(false);
      setStatus('idle');

      if (reading === null) {
        setLastRun(null);
        setPlayedIndex(0);
        setPlayedTick(null);
      } else {
        const finished: FinishedRun = { reading, rate };
        setLastRun(finished);
        onFinished?.(finished);
      }

      logRunReport({
        phraseId: range.id,
        state,
        mode: handsAsked(range),
        bpm,
        playbackRate: rate,
        outcome: reading === null ? 'cancelled' : passOutcome(reading),
        report: reading,
      });
    };

    // Tout ce qui précède a été armé (statut, capture, FSM, métronome,
    // écoute des fausses notes) avant que le transport soit pris : un
    // `run()` qui jette — `RangeRunnerBusyError` — laissait la vue en
    // « running » pour toujours.
    let pass: Promise<RunOutcome>;

    try {
      pass = rangeRunner.run({
        fromTick: range.startTick,
        toTick: range.endTick,
        rate,
        withCountIn: true,
        onTick: handleTick,
      });
    } catch {
      settle(null);

      return;
    }

    pass
      .catch((error: unknown): RunOutcome => ({ kind: 'error', error }))
      .then((outcome) => {
        // A `cancel()` may have been followed by a fresh `start()` while we
        // were awaiting; the new pass owns its own bookkeeping.
        if (runIdRef.current !== myRunId) {
          return;
        }

        const clock = clockRef.current;

        // Cancelled, errored, or completed without the playhead ever moving:
        // there is no reference frame to read the pass against, so we read
        // nothing rather than inventing a verdict.
        if (outcome.kind !== 'completed' || clock === null) {
          if (outcome.kind === 'error' && outcome.error instanceof RunStalledError) {
            reportStall(outcome.error);
          }

          settle(null);

          return;
        }

        const captured = journey.getSnapshot().capture.getPresses();
        const reading = runReading(groups, captured, clock, range.startTick, {
          ignorePitch,
        });

        if (WATCH_PLAYHEAD) {
          setDebug(
            summarise({
              captured,
              reading,
              groups,
              clock,
              firstTick: range.startTick,
              frozenMs,
              jumpedBack,
            }),
          );
        }

        settle(reading);
      });
  }, [
    journey,
    rangeRunner,
    range,
    groups,
    bpm,
    ppq,
    ticksPerBar,
    rate,
    state,
    ignorePitch,
    metronome,
    auditionSong,
    accompanyHand,
    onFinished,
    setSongVoice,
    setMetronomeActive,
    setBeatGridVisible,
    status,
    studio,
    clearStrayFlashes,
    stopStrayWatch,
    reportStall,
  ]);

  const cancel = useCallback(() => {
    if (rangeRunner === null || status === 'idle') {
      return;
    }

    rangeRunner.cancel();
  }, [rangeRunner, status]);

  const clear = useCallback(() => {
    setLastRun(null);
    setPlayedIndex(0);
    setPlayedTick(null);
    setStrayCount(0);
    setStalled(null);
  }, []);

  /**
   * Frame the passage on the roll for the whole step, not only for a pass.
   *
   * « Va au bout » is an instruction about a place, and the roll shows the
   * whole piece with nothing to say where the unit begins or ends. The layer
   * dims what is outside and rules the two boundaries — see
   * `piano-roll/work-range.ts`. Held for the life of the step so the player
   * can read the passage before playing it, and cleared on the way out.
   */
  useEffect(() => {
    if (studio === null) {
      return;
    }

    studio.setWorkRange({
      startTick: range.startTick,
      endTick: range.endTick,
    });

    return () => {
      studio.setWorkRange(null);
    };
  }, [studio, range.startTick, range.endTick]);

  // Restore the song and silence the click on unmount, in case a pass was in
  // flight (the player navigated away mid-run).
  useEffect(() => {
    return () => {
      setSongVoice('all');
      setMetronomeActive(false);
      setBeatGridVisible(false);
    };
  }, [setSongVoice, setMetronomeActive, setBeatGridVisible]);

  const strays = useMemo<LiveStrays>(
    () => ({ count: strayCount, flashing: strayFlash }),
    [strayCount, strayFlash],
  );

  // Mémoïsé : les vues le passent tel quel à des effets et des hooks
  // (raccourcis clavier…) qui ne doivent pas se ré-attacher à chaque rendu.
  return useMemo(
    () => ({
      status,
      start,
      cancel,
      clear,
      lastRun,
      playedIndex,
      playedTick,
      strays,
      debug,
      stalled,
    }),
    [
      status,
      start,
      cancel,
      clear,
      lastRun,
      playedIndex,
      playedTick,
      strays,
      debug,
      stalled,
    ],
  );
}

// ── The pass, from underneath ────────────────────────────────────

/** Off in production: the panel that shows it is dev-only too. */
const WATCH_PLAYHEAD = process.env.NODE_ENV !== 'production';

function summarise(input: {
  captured: readonly RecordedPress[];
  reading: RunReading;
  groups: readonly PracticeGroup[];
  clock: RunClock;
  firstTick: number;
  frozenMs: number;
  jumpedBack: number;
}): RunDebug {
  const { captured, reading, groups, clock, firstTick } = input;
  const beatOf = (tMs: number): number =>
    Math.round(((tMs - clock.anchorMs) / clock.beatMs) * 10) / 10;

  const inRun = captured
    .filter((p) => p.tMs >= clock.anchorMs)
    .map((p) => p.tMs)
    .sort((a, b) => a - b);
  const stop = reading.interruptions[0] ?? null;
  const at = onsetTimes(groups, clock, firstTick);
  const stopMs = stop === null ? null : at[stop.groupIndex];
  const before =
    stopMs === null ? null : inRun.filter((t) => t < stopMs).pop() ?? clock.anchorMs;
  const after = stopMs === null ? null : inRun.find((t) => t >= stopMs) ?? null;

  return {
    presses: inRun.length,
    pressesBeforeAnchor: captured.length - inRun.length,
    onsets: groups.length,
    honoured: reading.honoured.filter(Boolean).length,
    strays: reading.strays.length,
    stopAtBeat: stopMs === null ? null : beatOf(stopMs),
    stopFromBeat: before === null ? null : beatOf(before),
    stopToBeat: after === null ? null : beatOf(after),
    seconds:
      Math.round(
        ((groups.length === 0
          ? 0
          : ((groups[groups.length - 1].startTick - firstTick) / clock.ppq) *
            clock.beatMs) /
          1000) *
          10,
      ) / 10,
    playheadFrozenMs: Math.round(input.frozenMs),
    playheadJumpedBack: Math.round(input.jumpedBack),
  };
}
